/**
 * Represents a BottomSheet component that can be shown, hidden, and resized interactively.
 * Enlace al confluence:
 * https://thecocktail.atlassian.net/wiki/spaces/IBE/pages/4270293015/Bottom+Sheet
 */
import { currentDevice } from '../../core/utils/currentDevice'
import on from '../../utils/events_delegation'
import { vhToPx } from '../../utils/unit_conversion'

window.bsInstances = []

export class BottomSheet {
  /**
   * @param {Object} config - Configuration object for the BottomSheet.
   * @param {HTMLElement} config.showButton - The button that triggers the BottomSheet to open.
   * @param {String} config.sheetId - The id of the BottomSheet to open.
   * @param {number|string} [config.sheetHeight='auto'] - Default height in 'auto' or a specific value in vh.
   * @throws {Error} Throws an error if `showButton` is not provided.
   * @throws {Error} Throws an error if the `sheetSelector` dataset is not found on `showButton`.
   */

  static instances = []

  constructor(config = {}) {
    if (!config.showButton && !config.sheetId) {
      throw new Error(
        "The configuration is missing a required property. Please provide either 'showButton' or 'sheetId'."
      )
    }

    // Set / parse sheetHeight
    if (config.sheetHeight === undefined || config.sheetHeight === 'auto') {
      config.sheetHeight = 'auto'
    } else {
      const parsedHeight = parseInt(config.sheetHeight, 10)
      if (!isNaN(parsedHeight)) {
        config.sheetHeight = parsedHeight
      }
    }

    if (config.showButton) {
      this.showBtn = config.showButton
      this.sheetId = config.sheetId || this.showBtn.dataset.sheetSelector
      this.showBtn.setAttribute('aria-haspopup', 'true')
      this.showBtn.setAttribute('aria-expanded', 'false')
    }

    if (config.showButton && !this.sheetId && !config.sheetId) {
      throw new Error("The 'sheetSelector' dataset in 'showButton' is required.")
    }

    this.config = config

    if (config.sheetId) {
      this.sheetId = config.sheetId
    }

    this.bottomSheet = document.querySelector(`#${this.sheetId}`)
    this.sheetOverlay = this.bottomSheet.querySelector('.js-sheet-overlay')
    this.sheetContent = this.bottomSheet.querySelector('.js-sheet-content')
    this.sheetBody = this.bottomSheet.querySelector('.js-sheet-body')
    this.sheetFooter = this.bottomSheet.querySelector('.js-sheet-footer')
    this.sheetFooterCtas = this.bottomSheet.querySelector('.js-sheet-footer-ctas')
    this.dragIcon = this.bottomSheet.querySelector('.js-drag-icon')
    this.isDragging = false
    this.eventListeners = {} // Object to store event listeners

    this.bottomSheet.setAttribute('aria-modal', 'true')
    this.bottomSheet.inert = true

    if (config.testSelector) {
      this.bottomSheet.dataset.test = config.testSelector
    }

    // Append bottom sheet to body before show to ensure correct positioning
    if (this.bottomSheet.parentNode !== document.body) {
      document.body.appendChild(this.bottomSheet)
    }

    // Initial height setup
    this.calcInitialSheetHeight()
    this.triggeringElementOnOpen = null
    this.init()
    BottomSheet.instances.push(this)
    window.bsInstances.push(this)
  }

  getBottomSheetElem() {
    return this.bottomSheet
  }

  calcInitialSheetHeight() {
    // Initial height setup
    this.sheetContent.style.height = 'auto'
    const maxHeight = this.getWindowMaxHeight()

    let newHeight
    if (this.config.sheetHeight === 'auto') {
      const sheetContentHeight = this.sheetContent.clientHeight
      const footerHeight = this.sheetFooter ? this.sheetFooter.clientHeight : 0
      const currentSheetHeight = sheetContentHeight + footerHeight

      // We prevent the height from shrinking if an initialSheetHeight already exists.
      if (this.initialSheetHeight) {
        newHeight = Math.max(this.initialSheetHeight, Math.min(currentSheetHeight, maxHeight))
      } else {
        newHeight = Math.min(currentSheetHeight, maxHeight)
      }
    } else {
      newHeight = vhToPx(this.config.sheetHeight)
    }

    if (this.sheetBody && this.sheetFooterCtas) {
      const footerCtasHeight = this.sheetFooterCtas.clientHeight
      this.sheetBody.style.paddingBottom = `${footerCtasHeight}px`
    }

    if (!this.initialSheetHeight) {
      this.initialSheetHeight = newHeight
    }

    this.sheetHeight = newHeight
    this.updateSheetHeight(this.sheetHeight)
  }

  getWindowMaxHeight() {
    return Math.max(window.innerHeight, window.visualViewport.height, this.bottomSheet.clientHeight)
  }

  /**
   * Registers a listener for a specific event.
   * @param {string} eventName The name of the event to listen for.
   * @param {Function} callback The function to be called when the event is triggered.
   */
  addEventListener(eventName, callback) {
    if (!this.eventListeners[eventName]) {
      this.eventListeners[eventName] = []
    }
    this.eventListeners[eventName].push(callback)
  }

  /**
   * Removes a listener for a specific event.
   * @param {string} eventName The name of the event.
   * @param {Function} callback The callback function to remove.  Must be the same function that was registered.
   */
  removeEventListener(eventName, callback) {
    if (this.eventListeners[eventName]) {
      this.eventListeners[eventName] = this.eventListeners[eventName].filter(listener => listener !== callback)
    }
  }

  /**
   * Triggers/emits a specific event.
   * @param {string} eventName The name of the event to trigger.
   */
  triggerEvent(eventName) {
    if (this.eventListeners[eventName]) {
      this.eventListeners[eventName].forEach(listener => listener(this.bottomSheet))
    }
  }

  /**
   * Appends a new element to the end of the body if the given element is NOT the last element with the class 'bottom-sheet'
   */
  setAsLastInBody() {
    return new Promise(resolve => {
      const bottomSheetElements = document.querySelectorAll('.bottom-sheet')

      // Determine if the given element is the last one.
      const isLast = this.bottomSheet === bottomSheetElements[bottomSheetElements.length - 1]

      // If it's NOT the last element, append a new element to the body.
      if (!isLast) {
        document.body.appendChild(this.bottomSheet)

        // Resolve the promise after the appendChild operation is complete.
        // appendChild is synchronous, so it completes immediately.
        resolve()
      } else {
        // If it's already the last element, resolve immediately.
        resolve()
      }
    })
  }

  /**
   * Shows the BottomSheet and disables body scrolling.
   */
  async show(triggeringElement = null, callback) {
    await this.setAsLastInBody()
    this.triggeringElementOnOpen = triggeringElement || this.showBtn || document.activeElement

    ScrollLock.on()

    // Force a repaint before adding the animation class
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Double requestAnimationFrame
        this.bottomSheet.classList.remove('is-hidden')
        this.calcInitialSheetHeight()
        this.bottomSheet.classList.add('is-visible')
        if (this.showBtn) {
          this.showBtn.setAttribute('aria-expanded', 'true')
        }

        // Make the bottom sheet accessible and interactive
        this.bottomSheet.inert = false

        // Move focus to the first focusable element inside the bottom sheet
        const firstFocusable = this.bottomSheet.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (firstFocusable) {
          firstFocusable.focus()
        }
      })
    })
    document.documentElement.style.overflowY = 'hidden'

    this.updateSheetHeight(this.sheetHeight)

    this.triggerEvent('show')
    // Handle callback if it was passed as the first argument (due to old signature)
    if (typeof triggeringElement === 'function' && callback === undefined) {
      triggeringElement(this.bottomSheet)
    } else if (callback && typeof callback === 'function') {
      callback(this.bottomSheet)
    }
  }

  /**
   * Updates the height of the BottomSheet content.
   * @param {number} height - The new height of the BottomSheet.
   */
  updateSheetHeight(height) {
    const minHeight = 100
    const maxHeight = this.getWindowMaxHeight()
    const validatedHeight = Math.min(Math.max(height, minHeight), maxHeight)

    this.sheetContent.style.height = `${validatedHeight}px`
    this.bottomSheet.classList.toggle('is-fullscreen', validatedHeight === maxHeight)
  }

  /**
   * Hides the BottomSheet and restores body scrolling.
   */
  hide() {
    ScrollLock.off()
    this.triggerEvent('close')
    if (this.triggeringElementOnOpen && typeof this.triggeringElementOnOpen.focus === 'function') {
      this.triggeringElementOnOpen.focus()
      this.triggeringElementOnOpen = null
    }

    this.bottomSheet.classList.remove('is-visible')
    document.documentElement.style.overflowY = 'auto'
    this.bottomSheet.inert = true

    if (this.showBtn) {
      this.showBtn.setAttribute('aria-expanded', 'false')
    }

    // Reset height to initial value
    this.updateSheetHeight(this.initialSheetHeight)

    setTimeout(() => {
      this.bottomSheet.classList.add('is-hidden')
    }, 300)
  }

  /**
   * Starts the drag operation to resize the BottomSheet.
   * @param {MouseEvent|TouchEvent} e - The drag start event.
   */
  dragStart(e) {
    this.isDragging = true
    this.startY = e.pageY || e.touches?.[0].pageY
    this.startHeight = parseInt(this.sheetContent.style.height, 10)

    this.bottomSheet.classList.add('is-dragging')

    this.bottomSheet.addEventListener('mousemove', (this.draggingBound = this.dragging.bind(this)))
    this.bottomSheet.addEventListener('mouseup', (this.dragStopBound = this.dragStop.bind(this)))
    this.bottomSheet.addEventListener('touchmove', this.draggingBound, { passive: false })
    this.bottomSheet.addEventListener('touchend', this.dragStopBound, { passive: true })
  }

  /**
   * Handles the drag operation and adjusts the BottomSheet height dynamically.
   * @param {MouseEvent|TouchEvent} e - The drag event.
   */
  dragging(e) {
    if (!this.isDragging) return
    const delta = this.startY - (e.pageY || e.touches?.[0].pageY)
    const newHeight = this.startHeight + delta
    this.updateSheetHeight(newHeight)
  }

  /**
   * Ends the drag operation and determines the final state of the BottomSheet.
   */
  dragStop() {
    if (!this.isDragging) return

    this.isDragging = false
    this.bottomSheet.classList.remove('is-dragging')

    const sheetHeight = parseInt(this.sheetContent.style.height, 10)
    if (sheetHeight < this.startHeight) {
      if (this.startHeight === this.initialSheetHeight) {
        this.hide()
      } else {
        this.updateSheetHeight(this.initialSheetHeight)
      }
    } else {
      const maxHeight = this.getWindowMaxHeight()
      this.updateSheetHeight(maxHeight)
    }

    this.removeDragListeners()
  }

  /**
   * Removes drag event listeners.
   */
  removeDragListeners() {
    this.bottomSheet.removeEventListener('mousemove', this.draggingBound)
    this.bottomSheet.removeEventListener('mouseup', this.dragStopBound)
    this.bottomSheet.removeEventListener('touchmove', this.draggingBound)
    this.bottomSheet.removeEventListener('touchend', this.dragStopBound)
  }

  /**
   * Adds all necessary event listeners to the BottomSheet and related elements.
   */
  addEventListeners() {
    this.dragIcon.addEventListener('mousedown', this.dragStart.bind(this))
    this.dragIcon.addEventListener('touchstart', this.dragStart.bind(this), { passive: true })
    this.sheetOverlay.addEventListener('click', this.hide.bind(this))

    if (this.showBtn) {
      // Check if showBtn exists
      this.showBtn.addEventListener('click', () => this.show(this.showBtn))
      this.showBtn.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          this.show(this.showBtn)
        }
      })
    }
    if (currentDevice.isSafariMobile) {
      window.addEventListener('resize', () => {
        this.calcInitialSheetHeight()
      })
    }

    this.bottomSheet.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        this.hide()
      }
    })

    // Close instance to elements that have with `data-bottom-sheet-close`.
    on({
      eventName: 'click',
      selector: `#${this.sheetId} [data-bottom-sheet-close]`,
      handler: event => {
        const targetSheet = event.target.closest('.bottom-sheet')
        if (targetSheet === this.bottomSheet) {
          this.hide()
        }
      },
    })
  }

  /**
   * Checks if the BottomSheet is currently visible.
   * @returns {boolean} True if the BottomSheet is visible, false otherwise.
   */
  isVisible() {
    return this.bottomSheet.classList.contains('is-visible')
  }

  /**
   * Closes the BottomSheet programmatically.
   */
  close() {
    this.hide()
  }

  /**
   * Closes all BottomSheet programmatically.
   */
  static closeAll() {
    BottomSheet.instances.forEach(instance => {
      if (!instance.bottomSheet) return
      instance.close()
    })
  }

  /**
   * Initializes the BottomSheet by adding event listeners.
   */
  init() {
    this.addEventListeners()

    const observer = new MutationObserver(() => {
      this.sheetHeight = this.sheetContent.clientHeight
      this.updateSheetHeight(this.sheetHeight)
    })

    observer.observe(this.sheetContent, { childList: true, subtree: true })
  }

  /**
   * Removes all event listeners and clears references to DOM elements.
   */
  destroy() {
    this.dragIcon.removeEventListener('mousedown', this.dragStart.bind(this))
    this.dragIcon.removeEventListener('touchstart', this.dragStart.bind(this))
    this.sheetOverlay.removeEventListener('click', this.hide.bind(this))
    this.showBtn?.removeEventListener('click', this.show.bind(this))
    if (currentDevice.isSafariMobile) {
      window.removeEventListener('resize', this.calcInitialSheetHeight)
    }

    this.removeDragListeners()

    this.bottomSheet = null
    this.sheetOverlay = null
    this.sheetContent = null
    this.dragIcon = null
    this.showBtn = null
  }

  /**
   * Updates the content of a specific section of the BottomSheet with the given HTML.
   * @param {string} newHTML - The new HTML to inject into the BottomSheet.
   * @param {string} [target='body'] - The target section ('header', 'body', or 'footer'). Defaults to 'body'.
   */
  updateContent(newHTML, target = 'body') {
    const targetElement = this.bottomSheet.querySelector(`.js-sheet-${target}`)
    if (targetElement) {
      targetElement.innerHTML = newHTML
      this.sheetHeight = this.sheetContent.clientHeight
      this.updateSheetHeight(this.sheetHeight)
    } else {
      console.error(`Error: sheet-${target} element not found. Target provided: ${target}`)
    }
  }
}

// Creates a bottom sheet
// example:
/*
const exampleSheet = createBottomSheet({
  showButton: document.querySelector('#sheet-cta'),
  onShow: (sheet, sheetInstance) => {
    console.log('Sheet has been opened')
  },
  onClose: () => {
    console.log('Sheet has been closed')
  },
})
*/
export const createBottomSheet = config => {
  if (!config) return

  const sheetInstance = new BottomSheet(config)

  if (!sheetInstance) return

  if (config.onShow) {
    sheetInstance.addEventListener('show', bottomSheet => {
      config.onShow(bottomSheet, sheetInstance)
    })
  }

  if (config.onClose) {
    sheetInstance.addEventListener('close', bottomSheet => {
      config.onClose(bottomSheet, sheetInstance)
    })
  }

  return sheetInstance
}

// Creates and shows a bottom sheet with html passed as param in config.html
// example:
// createAndShowHtmlSheet({ html: '<h1>Hello!</h1>' })
const SHEET_TEMPLATE_ID = 'bottom-sheet-template'
export const createAndShowHtmlSheet = config => {
  if (!config || (config && !config.html)) return

  config.sheetClass ??= ''

  const randomId = new Date().getTime()
  const sheetId = `bottom-sheet-${randomId}`
  const sheetTemplate = document.querySelector(`#${SHEET_TEMPLATE_ID}`)

  if (!sheetTemplate) return

  let sheetTemplateHtml = sheetTemplate.innerHTML

  sheetTemplateHtml = sheetTemplateHtml
    .replaceAll('{{sheet_id}}', sheetId)
    .replaceAll('{{sheet_class}}', config.sheetClass)
    .replaceAll('{{sheet_content}}', config.html)

  document.body.insertAdjacentHTML('beforeend', sheetTemplateHtml)

  const sheetHeight = (config.sheetHeight ??= 'auto')
  const sheetInstance = createBottomSheet({ sheetId, sheetHeight })

  sheetInstance.addEventListener('show', bottomSheet => {
    // wait for next frame, two calls to make it secure

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        config.onShow && config.onShow(bottomSheet, sheetInstance)
        // content has changed, so height needs to be recalculated
        sheetInstance.calcInitialSheetHeight()
      })
    })
  })

  sheetInstance.addEventListener('close', bottomSheet => {
    if (config.onClose) {
      config.onClose(bottomSheet, sheetInstance)
    }

    // timeout to wait until the close animation ends
    setTimeout(() => {
      bottomSheet.remove()
      sheetInstance.destroy()
    }, 500)
  })

  sheetInstance.show(document.activeElement)

  return sheetInstance
}

export default BottomSheet
